import { vueData } from '@/VueData';
import CajasService from '@/api/services/cajas.service';
import { useRestaurantStore } from '@/stores/restaurant.store';
import { markRaw, ref, watch } from 'vue';
import {
  StorageSerializers,
  useDebounceFn,
  useLocalStorage,
  useThrottleFn,
  whenever,
} from '@vueuse/core';
import TablesService from '@/api/services/tables.service';
import { TaxDisplayMode } from '@/enums/TaxDisplayMode';
import { useSocket } from './use-socket';
import { WsServerEvents } from '@/const/websocket.consts';
import { ITable } from '@/interfaces/ITable';
import { ISector } from '@/interfaces/ISector';
import { ICaja } from '@/interfaces/ICaja';
import CustomFieldsService from '@/api/services/custom-fields.service';
import ICustomField from '@/interfaces/ICustomField';
import { IBranch } from '@/interfaces/IBranch';
import DeliveryZonesService from '@/api/services/delivery-zones.service';
import InvoicingDataService from '@/api/services/invoicing/invoicing-data.service';
import { Globals } from '@/modules/Globals';
import { IMember, MemberRole } from '@/interfaces/IMember';
import { useAuthStore } from './use-auth';
import { AlertType } from '@/interfaces/IAlert';

export function useRestaurant(main = false) {
  const realtime = useSocket();
  const store = useRestaurantStore();
  const authStore = main ? useAuthStore() : null;

  async function reloadCajas() {
    const cajas = await CajasService.getAll(
      vueData.restaurant.id!,
      vueData.branch.id!
    );

    store.setCajas(cajas);

    return cajas;
  }

  async function reloadTables() {
    const tables = await TablesService.getAll(
      vueData.restaurant.id!,
      vueData.branch.id!
    );

    store.setTables(tables);

    return tables;
  }

  async function reloadCustomFields() {
    const customFields = await CustomFieldsService.getAll(
      vueData.restaurant.id!,
      vueData.branch.id!
    );

    store.setCustomFields(customFields);

    return customFields;
  }

  async function reloadDeliveryZones() {
    const zones = await DeliveryZonesService.getByBranch(vueData.branch.id!);

    store.setDeliveryZones(zones);

    return zones;
  }

  async function reloadInvoicingData() {
    const data = await InvoicingDataService.getByBranch({
      restaurantId: vueData.restaurant.id!,
      branchId: vueData.branch.id!,
      platform: 'ar-afip',
    });

    store.setInvoicingData(data);

    return data;
  }

  async function reloadAll() {
    if (!vueData.branch || store.loadingBranchId === vueData.branch.id) return;

    try {
      store.setLoadingBranchId(vueData.branch.id);
      await Promise.all([
        reloadCajas(),
        reloadTables(),
        store.loadSectors(),
        reloadCustomFields(),
        reloadDeliveryZones(),
        reloadInvoicingData(),
      ]);
    } finally {
      store.setLoadingBranchId(null);
    }
  }

  async function clearAll() {
    store.setCajas(null);
    store.setTables(null);
    store.setSectors(null);
    store.setCustomFields(null);
    store.setDeliveryZones(null);
  }

  if (main) {
    whenever(realtime.isConnected, (connected) => {
      if (vueData.branch) {
        reloadCustomFields();
      }
    });

    watch(
      () => store.caja,
      (newCaja, oldCaja) => {
        const { restaurant, branch } = vueData;
        if (restaurant && branch) {
          const storedCajaId = useLocalStorage<number>(
            `caja-${restaurant.id}-${branch.id}`,
            null,
            { serializer: StorageSerializers.number }
          );

          if (newCaja) {
            storedCajaId.value = newCaja.id;
          } else if (oldCaja) {
            storedCajaId.value = null;
          }
        }
      },
      {
        immediate: true,
      }
    );

    watch(
      () => store.cajas,
      (newCajas, oldCajas) => {
        const { restaurant, branch } = vueData;
        let cajaFound = false;

        if (restaurant && branch) {
          const storedCajaId = useLocalStorage<number>(
            `caja-${restaurant.id}-${branch.id}`,
            null,
            { serializer: StorageSerializers.number }
          ).value;

          if (newCajas && storedCajaId) {
            // Skip disabled cajas: restoring one would let the user keep
            // charging into an archived caja (nothing downstream re-checks
            // `enabled`). Clearing forces the "Seleccione una caja" prompt.
            const caja = newCajas.find(
              (c) => c.id === storedCajaId && c.enabled
            );
            if (caja) {
              cajaFound = true;
              store.setCaja(caja);
            }
          }
        }

        if (!cajaFound) {
          store.setCaja(null);
        }
      },
      {
        immediate: true,
      }
    );

    function hasBranchPermission(
      branchId: number,
      role?: IMember | null
    ): boolean {
      if (!role) role = authStore!.currentRole;

      if (!role) return false;

      // Admins and Owners have access to all branches
      if (role.type === MemberRole.Admin || role.type === MemberRole.Owner) {
        return true;
      }

      // Check if user has allBranches permission
      if (role.allBranches) {
        return true;
      }

      // Check if the branch is in the user's allowed branches
      return role.branches?.some((branch) => branch.id === branchId) ?? false;
    }

    function onTableUpdate(eventData: {
      branchId: number;
      entity: ITable;
      action: 'create' | 'update' | 'delete';
    }) {
      const tables = store.tables;

      if (!tables) return;

      if (eventData.branchId !== store.selectedBranch?.id) return;

      const existingTableIndex = tables.findIndex(
        (t) => t.id === eventData.entity.id
      );

      // tables is a shallowRef list: rebuild it immutably so consumers react.
      switch (eventData.action) {
        case 'create':
          if (existingTableIndex === -1) {
            store.setTables([...tables, eventData.entity]);
          }
          break;
        case 'update':
          if (existingTableIndex !== -1) {
            store.setTables(
              tables.map((t) =>
                t.id === eventData.entity.id ? { ...t, ...eventData.entity } : t
              )
            );
          } else {
            // Upsert: a missed create must not drop later updates.
            store.setTables([...tables, eventData.entity]);
          }
          break;
        case 'delete':
          if (existingTableIndex !== -1) {
            store.setTables(tables.filter((t) => t.id !== eventData.entity.id));
          }
          break;
      }
    }

    function onSectorUpdate(eventData: {
      branchId: number;
      entity: ISector;
      action: 'create' | 'update' | 'delete';
    }) {
      const sectors = store.sectors;

      if (!sectors) return;

      if (eventData.branchId !== store.selectedBranch?.id) return;

      const exists = sectors.some((s) => s.id === eventData.entity.id);
      let newSectors: ISector[];

      // sectors is a shallowRef list: rebuild it immutably so consumers react.
      switch (eventData.action) {
        case 'create':
          if (exists) return;
          newSectors = [...sectors, eventData.entity];
          break;
        case 'update':
          // Upsert: a missed create must not drop later updates.
          newSectors = exists
            ? sectors.map((s) =>
                s.id === eventData.entity.id ? { ...s, ...eventData.entity } : s
              )
            : [...sectors, eventData.entity];
          break;
        case 'delete':
          if (!exists) return;
          newSectors = sectors.filter((s) => s.id !== eventData.entity.id);
          break;
      }

      newSectors.sort((a, b) => a.index - b.index);
      store.setSectors(newSectors);
    }

    function onCajaUpdate(eventData: {
      branchId: number;
      entity: ICaja;
      action: 'create' | 'update' | 'delete';
    }) {
      if (Globals.IsLocalhost) {
        console.log('onCajaUpdate', eventData);
      }
      const cajas = store.cajas;

      if (!cajas) return;

      if (eventData.branchId !== store.selectedBranch?.id) return;

      const existingCajaIndex = cajas.findIndex(
        (c) => c.id === eventData.entity.id
      );

      switch (eventData.action) {
        case 'create':
          if (existingCajaIndex === -1) {
            cajas.push(eventData.entity);
          }
          break;
        case 'update':
          if (existingCajaIndex !== -1) {
            Object.assign(cajas[existingCajaIndex], eventData.entity);
          }
          break;
        case 'delete':
          if (existingCajaIndex !== -1) {
            cajas.splice(existingCajaIndex, 1);
          }
          break;
      }
    }

    function onBranchUpdate(eventData: {
      entity: IBranch;
      action: 'create' | 'update' | 'delete';
    }) {
      if (eventData.entity.restaurantId !== store.restaurant?.id) return;

      // Skip if user doesn't have permission for this branch
      if (!hasBranchPermission(eventData.entity.id)) {
        if (Globals.IsLocalhost) {
          console.debug(
            `[Branch Update] Skipping update for branch ${eventData.entity.id} - no permission`
          );
        }
        return;
      }

      const branches = store.branches;

      if (!branches) return;

      const existingBranchIndex = branches.findIndex(
        (b) => b.id === eventData.entity.id
      );

      switch (eventData.action) {
        case 'create':
          if (existingBranchIndex === -1) {
            branches.push(eventData.entity);
          }
          break;
        case 'update':
          if (existingBranchIndex !== -1) {
            Object.assign(branches[existingBranchIndex], eventData.entity);
          } else {
            // Only add if it's an update and user has permission (already checked above)
            branches.push(eventData.entity);
          }
          break;
        case 'delete':
          if (existingBranchIndex !== -1) {
            branches.splice(existingBranchIndex, 1);

            if (store.selectedBranch?.id === eventData.entity.id) {
              // Switch to first active branch the user has access to
              const activeBranches = branches.filter((b) => b.active);
              store.setSelectedBranch(activeBranches[0] || null);
            }
          }
          break;
      }
    }

    function onCustomFieldsUpdate(eventData: {
      branchId: number;
      customFields: ICustomField[];
    }) {
      if (eventData.branchId !== store.restaurant?.id) return;

      store.setCustomFields(eventData.customFields);
    }

    watch(
      () => store.restaurant,
      (newRestaurant, oldRestaurant) => {
        if (newRestaurant?.id === oldRestaurant?.id) return;

        const socket = realtime.socket.value!;

        if (oldRestaurant) {
          socket.removeEventListener(
            `r:${oldRestaurant.id}:${WsServerEvents.TABLE_UPDATED}`,
            onTableUpdate
          );
          socket.removeEventListener(
            `r:${oldRestaurant.id}:${WsServerEvents.SECTOR_UPDATED}`,
            onSectorUpdate
          );
          socket.removeEventListener(
            `r:${oldRestaurant.id}:${WsServerEvents.CAJA_UPDATED}`,
            onCajaUpdate
          );
          socket.removeEventListener(
            `r:${oldRestaurant.id}:${WsServerEvents.BRANCH_UPDATED}`,
            onBranchUpdate
          );
          socket.removeEventListener(
            `r:${oldRestaurant.id}:${WsServerEvents.CUSTOM_FIELDS_UPDATED}`,
            onCustomFieldsUpdate
          );
        }

        if (newRestaurant) {
          socket.addEventListener(
            `r:${newRestaurant.id}:${WsServerEvents.TABLE_UPDATED}`,
            onTableUpdate
          );
          socket.addEventListener(
            `r:${newRestaurant.id}:${WsServerEvents.SECTOR_UPDATED}`,
            onSectorUpdate
          );
          socket.addEventListener(
            `r:${newRestaurant.id}:${WsServerEvents.CAJA_UPDATED}`,
            onCajaUpdate
          );
          socket.addEventListener(
            `r:${newRestaurant.id}:${WsServerEvents.BRANCH_UPDATED}`,
            onBranchUpdate
          );
          socket.addEventListener(
            `r:${newRestaurant.id}:${WsServerEvents.CUSTOM_FIELDS_UPDATED}`,
            onCustomFieldsUpdate
          );
        }
      },
      {
        immediate: true,
      }
    );

    watch(
      () => store.selectedBranch,
      (newBranch, oldBranch) => {
        if (newBranch?.id === oldBranch?.id) return;

        reloadAll().catch((err) => {
          console.error('Error reloading all on branch change', err);
        });
      }
    );

    // Watch for role changes to validate selected branch
    watch(
      () => authStore?.currentRole,
      (newRole, oldRole) => {
        if (!newRole || !store.selectedBranch || !store.branches) return;

        // Check if user still has access to the selected branch
        if (!hasBranchPermission(store.selectedBranch.id, newRole)) {
          // Switch to first accessible active branch
          const accessibleBranches = store.branches.filter(
            (b) => b.active && hasBranchPermission(b.id, newRole)
          );

          if (accessibleBranches.length > 0) {
            store.setSelectedBranch(accessibleBranches[0]);
            vueData.addAlert(
              'Se cambió a otra sucursal debido a cambios en los permisos',
              AlertType.Info
            );
          } else {
            store.setSelectedBranch(null);
          }
        }
      }
    );
  }

  //#region Useful methods

  function applyTax({
    price,
    partial,
    forced,
  }: {
    price: number;
    partial: boolean;
    forced?: boolean;
  }) {
    const r = store.restaurant;
    const s = store.restaurantSettings;

    if (!r?.tax || !s || s.taxMode === TaxDisplayMode.NoTax) return price;

    if (forced) {
      return price * (1 + r.tax / 100);
    }

    if (partial) {
      if (
        s.taxMode === TaxDisplayMode.AddAndShow ||
        s.taxMode === TaxDisplayMode.SilentAdd
      ) {
        return price * (1 + r.tax / 100);
      }
    } else {
      if (s.taxMode === TaxDisplayMode.AddAtInvoice) {
        return price * (1 + r.tax / 100);
      }
    }

    return price;
  }

  //#endregion

  return markRaw({
    reloadCajas,
    reloadAll,
    clearAll,
    applyTax,
  });
}
